import React, { useState } from 'react';
import { useAddEmailMutation } from '../services/signupApi';
import { useAppDispatch } from '../hooks/redux';
import { showNotification } from '../store/uiSlice';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../config/firebase';
import { trackSignupSuccess, trackSignupError, trackFormInteraction } from '../services/clarityService';
import { useNavigate } from 'react-router-dom';



interface EmailSignupFormProps {
  formLocation: string;
  onSuccess?: () => void;
  customClass?: string;
  inputClass?: string;
  buttonClass?: string;
  buttonText?: string;
  isSignupButton?: boolean;
}

const EmailSignupForm: React.FC<EmailSignupFormProps> = ({ 
  formLocation, 
  onSuccess, 
  customClass, 
  inputClass, 
  buttonClass, 
  buttonText = 'Get Early Access',
  isSignupButton = false 
}) => {
  const [email, setEmail] = useState('');
  const [hasInteracted, setHasInteracted] = useState(false);
  const [addEmail, { isLoading: isAdding }] = useAddEmailMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isLoading = isAdding;

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    // Track first interaction with the form
    if (!hasInteracted && newEmail.length > 0) {
      setHasInteracted(true);
      trackFormInteraction('start_typing', formLocation);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSignupButton) {
      // Navigate to signup page
      navigate('/signup');
      return;
    }
    
    if (!email) return;

    // Track form submission attempt
    trackFormInteraction('submit_attempt', formLocation);

    try {
      // Only add new email, do not check existence
      await addEmail({ 
        email: email.toLowerCase().trim(), 
        formLocation,
        source: 'landing-page',
        userAgent: navigator.userAgent,
        referrer: document.referrer || 'direct',
      }).unwrap();

      // Log successful signup
      logEvent(analytics, 'sign_up', {
        method: 'email',
        email_domain: email.split('@')[1],
        form_location: formLocation,
      });

      // Track signup success with Clarity
      trackSignupSuccess(email.toLowerCase().trim(), formLocation, 'landing-page');

      setEmail('');
      setHasInteracted(false);
      dispatch(showNotification({ 
        message: "✅ You're on the list! We'll notify you when TenantTerms launches.", 
        severity: 'success' 
      }));
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Signup error:', error);
      dispatch(showNotification({ 
        message: 'Sorry, there was an error. Please try again or email us at hello@TenantTerms.com', 
        severity: 'error' 
      }));
      // Log error
      logEvent(analytics, 'signup_error', {
        error_message: error instanceof Error ? error.message : 'Unknown error',
        form_location: formLocation,
      });

      // Track signup error with Clarity
      trackSignupError(error instanceof Error ? error.message : 'Unknown error', formLocation);
    }
  };

  return (
    <form 
      onSubmit={handleSubmit} 
      className={customClass}
      style={{ width: '100%' }}
    >
      <input
        type="email"
        className={inputClass}
        placeholder="Enter your email"
        value={email}
        onChange={handleEmailChange}
        required
        disabled={isLoading}
        style={{ flex: 1 }}
      />
      <button
        type="submit"
        className={buttonClass}
        disabled={isLoading}
        style={{ minWidth: 150 }}
      >
        {isLoading ? <span>Loading...</span> : buttonText}
      </button>
    </form>
  );
};

export default EmailSignupForm; 